import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendContactForm } from '../../fetchers/contact';
import { validateRecaptchaToken } from '../../fetchers/recaptcha';
import ContactMenu from '../../components/ContactMenu';
import Footer from '../../components/Footer';
import FormErrorMessage from '../../components/FormErrorMessage';
import FormInput from '../../components/FormInput';
import FormTextArea from '../../components/FormTextArea';
import Menu from '../../components/Menu/Menu';
import MyHead from '../../components/MyHead';
import SecondaryButtonBig from '../../components/SecondaryButtonBig';
import Spinner from '../../components/Spinner';
import withRecaptcha from '../../components/withRecaptcha';
import { useAlert } from '../../context/AlertProvider';
import { getToken } from '../../helpers/auth';
import getRecaptchaToken from '../../utils/getRecaptchaToken';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const PressContacts: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { values, errors, handleChange, handleSubmit, resetForm } = useFormik({
    validateOnChange: false,
    initialValues: {
      first_name: '',
      last_name: '',
      phone: '',
      press: '',
      country: '',
      website: '',
      email: '',
      message: '',
      contact: ''
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string().required(t('REQUIRED_FIELD')),
      last_name: Yup.string().required(t('REQUIRED_FIELD')),
      phone: Yup.string().required(t('REQUIRED_FIELD')),
      press: Yup.string().required(t('REQUIRED_FIELD')),
      country: Yup.string().required(t('REQUIRED_FIELD')),
      website: Yup.string().required(t('REQUIRED_FIELD')),
      email: Yup.string().required(t('REQUIRED_FIELD')),
      message: Yup.string().required(t('REQUIRED_FIELD')),
      contact: Yup.string().required(t('REQUIRED_FIELD'))
    }),
    async onSubmit() {
      try {
        setLoading(true);

        // Validate recaptcha
        // await validateRecaptchaToken({
        //   token,
        //   recaptchaToken: await getRecaptchaToken()
        // });

        await sendContactForm({ token, formName: 'press', data: values });

        resetForm();

        alert.show(t('SEND_FORM_SUCCESS'));
      } catch (error: any) {
        alert.error(t('GENERIC_ERROR'));
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <>
      <MyHead />
      <Menu />
      <div className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <ContactMenu actual_page={'press-contacts'} />
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <div className="flex items-left justify-between mb-3 flex-col md:flex-row md:items-center">
              <h3 className="text-3xl font-sans">{t('CONTACT_US_PRESS')}</h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="first_name"
                    type="text"
                    placeholder={t('NAME')}
                    name="first_name"
                    value={values.first_name}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="first_name" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="last_name"
                    type="text"
                    placeholder={t('SURNAME')}
                    name="last_name"
                    value={values.last_name}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="last_name" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="email"
                    type="email"
                    placeholder={t('MAIL_ADDRESS')}
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="email" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="phone"
                    type="text"
                    placeholder={t('PHONE')}
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="phone" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="press"
                    type="text"
                    placeholder={t('PRESS_HEADING')}
                    name="press"
                    value={values.press}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="press" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="country"
                    type="text"
                    placeholder={t('COUNTRY')}
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="country" />
                </div>
              </div>

              <div>
                <div>
                  <FormInput
                    id="website"
                    type="text"
                    placeholder={t('WEBSITE')}
                    name="website"
                    value={values.website}
                    onChange={handleChange}
                  />

                  <FormErrorMessage errors={errors} name="website" />
                </div>
              </div>

              <div>
                <FormTextArea
                  id="message"
                  placeholder={t('MESSAGE')}
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                />

                <FormErrorMessage errors={errors} name="message" />
              </div>

              <div
                className={`relative flex items-center mt-2 py-2 px-3 border rounded ${
                  errors.contact ? ' border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  id="contact"
                  type="checkbox"
                  name="contact"
                  value={values.contact}
                  onChange={handleChange}
                />
                <div className="ml-2 text-sm">
                  <label htmlFor="contact">
                    <div
                      id="newsletter-description"
                      className="text-gray-500 cursor-pointer"
                    >
                      {t('FOOTER_NEWSLETTER_PRIVACY')}
                      <a
                        href={`https://www.iubenda.com/privacy-policy/${t(
                          'IUBENDA.POLICY_ID'
                        )}`}
                        className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                        title="Privacy Policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('PRIVACY_TITLE')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
              <div className="mt-6 max-w-lg">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND')} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default withRecaptcha(PressContacts);
